import * as eff from 'redux-saga/effects';
import createActionCreators from 'redux-resource-action-creators';

import {
  actionTypes,
} from '../actions';
import {
  getProfile,
  getResourceById,
  getResourceMappedList,
} from '../selectors';
import api from '../api';

import {
  throwError,
} from './helpers';
import {
  getExerciseNumber,
} from '../utils';

export const normalizedEventExercises = exercises => exercises.reduce(
  (acc, { exercise_properties: properties, exercise_results: results, ...rest }) => ({
    eventExercises: [
      ...acc.eventExercises,
      {
        ...rest,
        // add to every { letter: '', number: '' } from order_name
        ...getExerciseNumber(rest),
      },
    ],
    exerciseProperties: [
      ...acc.exerciseProperties,
      ...properties,
    ],
    exerciseResults: [
      ...acc.exerciseResults,
      ...results,
    ],
    multiLists: {
      exerciseResults: {
        ...acc.multiLists.exerciseResults,
        [`workout_exercise_${rest.workout_exercise_id}`]: results.map(r => r.id),
      },
      exerciseProperties: {
        ...acc.multiLists.exerciseProperties,
        [`workout_exercise_${rest.workout_exercise_id}`]: properties.map(r => r.id),
      },
    },
  }), {
  eventExercises: [],
  exerciseProperties: [],
  exerciseResults: [],
  multiLists: {},
},
);

export function* fetchWorkoutEvents({ params = {}, userId, fetchAll }) {
  const resourceType = 'workoutEvents';
  const actions = createActionCreators('read', {
    resourceType,
    requestKey: 'fetchWorkoutEvents',
  });
  let list = 'all';
  const requestParams: any = {
    qs: {
      ...params,
    },
  };

  if (userId) {
    list = userId.toString();
    requestParams.userId = userId;
  }

  try {
    yield eff.put(actions.pending());
    const workoutEvents = yield eff.call(
      api.getWorkoutEvents,
      requestParams,
    );
    yield eff.put(actions.succeeded({
      resources: workoutEvents,
      list,
    }));

    if (fetchAll && workoutEvents.length >= 50) {
      yield eff.fork(fetchWorkoutEvents, {
        params: {
          ...params,
          page: (params.page ?? 1) + 1,
        },
        userId,
        fetchAll,
      })
    }
  } catch (err) {
    yield eff.put(actions.failed({
      resources: [],
    }));
    yield eff.call(throwError, err);
  }
}

export default function* workoutEventFlow() {
  function* create({
    isNow,
    userId,
    payload,
    callbacks,
  }) {
    const actions = createActionCreators('create', {
      resourceType: 'workoutEvents',
      requestKey: 'createWorkoutEvent',
    });
    try {
      yield eff.put(actions.pending());
      const {
        name,
        ...body
      } = payload;

      if (!body.personal_workout_id) {
        const workout = yield eff.call(
          api.createEntity,
          {
            type: 'workout_templates',
            body: {
              name,
              is_visible: false,
            },
          },
        );
        const personalWorkout = yield eff.call(
          api.createEntity,
          {
            type: 'personal_workouts',
            body: {
              person_id: userId,
              workout_template_id: workout.id,
            },
          },
        );
        body.personal_workout_id = personalWorkout.id;
      }

      const response = yield eff.call(
        api.createEntity,
        {
          type: (body.repeat_type == 'daily' || body.repeat_type == 'weekly')
            ? 'workout_events/schedule_repeating'
            : 'workout_events',
          body,
        },
      );
      yield eff.put(actions.succeeded({
        resources: Array.isArray(response) ? response : [response],
        list: userId.toString(),
      }));
      callbacks?.onSuccess?.(response);

      const isPro = yield eff.select(
        getProfile('is_pro'),
      );
      yield eff.put({
        type: 'GC_ANALYTICS',
        eventKey: `${isPro ? 'pro' : 'client'}_${isNow ? 'started' : 'scheduled'}_workout`,
        attrs: response,
      });
    } catch (err) {
      yield eff.put(actions.failed({
        resources: [],
      }));
      callbacks?.onError?.(err);
      yield eff.call(throwError, err);
    }
  }

  function* update({
    id,
    userId,
    payload,
    callbacks,
  }) {
    const actions = createActionCreators('update', {
      resourceType: 'workoutEvents',
      requestKey: 'updateWorkoutEvent',
    });
    try {
      yield eff.put(actions.pending());
      const {
        name,
        ...body
      } = payload;

      if (!body.personal_workout_id) {
        const workout = yield eff.call(
          api.createEntity,
          {
            type: 'workout_templates',
            body: {
              name,
              is_visible: false,
            },
          },
        );
        const personalWorkout = yield eff.call(
          api.createEntity,
          {
            type: 'personal_workouts',
            body: {
              person_id: userId,
              workout_template_id: workout.id,
            },
          },
        );
        body.personal_workout_id = personalWorkout.id;
      }

      const response = yield eff.call(
        api.updateEntity,
        {
          id,
          type: 'workout_events',
          body,
        },
      );
      yield eff.put(actions.succeeded({
        resources: [response],
        list: userId.toString(),
      }));
      callbacks?.onSuccess?.(response);

      const isPro = yield eff.select(
        getProfile('is_pro'),
      );
      yield eff.put({
        type: 'GC_ANALYTICS',
        eventKey: `${isPro ? 'pro' : 'client'}_scheduled_workout`,
        attrs: response,
      });
    } catch (err) {
      yield eff.put(actions.failed({
        resources: [],
      }));
      callbacks?.onError?.(err);
      yield eff.call(throwError, err);
    }
  }

  function* syncFutureWorkoutEvents({
    events,
    callbacks,
  }) {
    const resourceType = 'workoutEvents';
    const actions = createActionCreators('update', {
      resourceType,
      requestKey: 'syncFutureWorkoutEvents',
    });

    try {
      yield eff.put(actions.pending());

      const updatedEvents = yield eff.all(
        events.map(event => eff.call(
          api.updateEntity,
          {
            id: event.id,
            type: 'workout_events',
            body: {
              begins_at: event.begins_at,
              ends_at: event.ends_at,
              person_id: event.person_id,
              personal_workout_id: event.personal_workout_id,
            },
          },
        )),
      );

      yield eff.put(actions.succeeded({
        resources: updatedEvents,
      }));
      callbacks?.onSuccess?.(updatedEvents);
    } catch (err) {
      yield eff.put(actions.failed({
        resources: [],
      }));
      callbacks?.onError?.(err);
      yield eff.call(throwError, err);
    }
  }

  function* getFull({ id }) {
    const requestKey = 'fetchFullEvent';
    const resourceType = 'workoutEvents';
    const actions = createActionCreators('read', {
      requestKey,
      resourceType,
    });

    try {
      yield eff.put(actions.pending());

      const {
        exercises,
        ...rest
      } = yield eff.call(
        api.getFullWorkoutEvent,
        {
          id,
        },
      );
      const {
        multiLists,
        eventExercises,
        exerciseProperties,
        exerciseResults,
      } = normalizedEventExercises(exercises);

      yield eff.put(actions.succeeded({
        resources: [rest],
        includedResources: {
          exercises: eventExercises,
          exerciseProperties,
          exerciseResults,
        },
        list: rest.person_id.toString(),
        multiLists,
        customLists: {
          exercises: `event_${id}`,
        },
      }));
    } catch (err) {
      yield eff.put(actions.failed({
        resources: [],
      }));
      yield eff.call(throwError, err);
    }
  }

  function* createResult({
    items,
    eventId,
    workoutExerciseId,
  }) {
    const actions = createActionCreators('create', {
      resourceType: 'exerciseResults',
      requestKey: 'createResultItems',
    });

    function* addItem(item, resultId) {
      try {
        const result = yield eff.call(
          api.createEntityItems,
          {
            id: resultId,
            type: 'exercise_results',
            body: item,
          },
        );

        return result;
      } catch (e) {
        yield eff.call(throwError, e);

        return null;
      }
    }

    try {
      yield eff.put(actions.pending());

      const result = yield eff.call(
        api.createEntity,
        {
          type: 'exercise_results',
          body: {
            workout_event_id: eventId,
            workout_exercise_id: workoutExerciseId,
          },
        },
      );

      const resultItems = yield eff.all(
        items.map(item => eff.call(addItem, item, result.id)),
      );

      yield eff.put(actions.succeeded({
        resources: [{
          ...result,
          exercise_result_items: resultItems.filter(item => item && item.id),
        }],
        list: `workout_exercise_${workoutExerciseId}`,
      }));
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));

      yield eff.call(throwError, e);
    }
  }

  function* removeAllResults({ eventId, callbacks }) {
    const resourceType = 'exerciseResults';
    const actions = createActionCreators('delete', {
      resourceType,
      requestKey: 'removeAllResults',
    });

    const allResults = yield eff.select(
      getResourceMappedList(resourceType),
    );

    const filtered = allResults.filter(
      r => r.workout_event_id === eventId,
    );

    try {
      yield eff.put(actions.pending());

      yield eff.all(
        filtered.map(item => eff.call(
          api.deleteEntity,
          {
            id: item.id,
            type: 'exercise_results',
          },
        )),
      );

      yield eff.put(actions.succeeded({
        resources: filtered.map(r => r.id),
      }));
      callbacks?.onSuccess?.();
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));
      callbacks?.onError?.(e);

      yield eff.call(throwError, e);
    }
  }

  function* finishEvent({ eventId, callbacks }) {
    const resourceType = 'workoutEvents';
    const actions = createActionCreators('update', {
      resourceType,
      requestKey: 'finishEvent',
    });
    const event = yield eff.select(getResourceById(resourceType, eventId));
    try {
      yield eff.put(actions.pending());

      const response = yield eff.call(api.updateEntity, {
        type: 'workout_events',
        id: eventId,
        body: { ...event, is_completed: true },
      });
      yield eff.put(actions.succeeded({
        resources: [response],
      }));
      callbacks?.onSuccess?.();

      const isPro = yield eff.select(
        getProfile('is_pro'),
      );
      yield eff.put({
        type: 'GC_ANALYTICS',
        eventKey: `${isPro ? 'pro' : 'client'}_completed_workout`,
        attrs: response,
      });
    } catch (e) {
      yield eff.put(actions.failed());
      callbacks?.onError?.(e);
      yield eff.call(throwError, e);
    }
  }

  yield eff.takeEvery(actionTypes.REQUEST_LOAD_WORKOUT_EVENTS, fetchWorkoutEvents);
  yield eff.takeEvery(actionTypes.REQUEST_CREATE_WORKOUT_EVENT, create);
  yield eff.takeEvery(actionTypes.REQUEST_UPDATE_WORKOUT_EVENT, update);
  yield eff.takeEvery(actionTypes.REQUEST_SYNC_FUTURE_WORKOUT_EVENTS, syncFutureWorkoutEvents);
  yield eff.takeEvery(actionTypes.REQUEST_LOAD_FULL_WORKOUT_EVENT, getFull);
  yield eff.takeEvery(actionTypes.REQUEST_CREATE_WORKOUT_EVENT_RESULT, createResult);
  yield eff.takeEvery(actionTypes.REQUEST_REMOVE_ALL_EVENT_RESULTS, removeAllResults);
  yield eff.takeEvery(actionTypes.REQUEST_FINISH_EVENT, finishEvent);
}
